<!-- src/components/odontogram/Odontogram.vue -->
<template>
  <div class="odontogram-container">
    <div v-if="loading" class="text-center py-4">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
    </div>
    
    <div v-else class="odontogram">
      <!-- Adult Odontogram -->
      <div class="odontogram-section adult-odontogram">
        <h5 class="section-title">Odontograma Adulto</h5>
        <div class="jaw upper-jaw">
          <div class="teeth-row">
            <div 
              v-for="toothNumber in adultUpperTeeth" 
              :key="toothNumber"
              class="tooth-container"
            >
              <div 
                class="tooth" 
                :class="getToothClass(toothNumber, 'adult')" 
                @click="selectTooth(toothNumber, 'adult')"
              >
                <span class="tooth-number">{{ toothNumber }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="jaw lower-jaw">
          <div class="teeth-row">
            <div 
              v-for="toothNumber in adultLowerTeeth" 
              :key="toothNumber"
              class="tooth-container"
            >
              <div 
                class="tooth" 
                :class="getToothClass(toothNumber, 'adult')" 
                @click="selectTooth(toothNumber, 'adult')"
              >
                <span class="tooth-number">{{ toothNumber }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Child Odontogram -->
      <div class="odontogram-section child-odontogram">
        <h5 class="section-title">Odontograma Infantil</h5>
        <div class="jaw upper-jaw">
          <div class="teeth-row">
            <div 
              v-for="toothNumber in childUpperTeeth" 
              :key="toothNumber"
              class="tooth-container"
            >
              <div 
                class="tooth" 
                :class="getToothClass(toothNumber, 'child')" 
                @click="selectTooth(toothNumber, 'child')"
              >
                <span class="tooth-number">{{ toothNumber }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="jaw lower-jaw">
          <div class="teeth-row">
            <div 
              v-for="toothNumber in childLowerTeeth" 
              :key="toothNumber"
              class="tooth-container"
            >
              <div 
                class="tooth" 
                :class="getToothClass(toothNumber, 'child')" 
                @click="selectTooth(toothNumber, 'child')"
              >
                <span class="tooth-number">{{ toothNumber }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
  
      <!-- Tooth information panel -->
      <div v-if="selectedTooth" class="tooth-info-panel">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="mb-0">
              {{ selectedTooth.type === 'adult' ? 'Diente Adulto' : 'Diente Infantil' }} 
              {{ selectedTooth.tooth_number }}
            </h5>
            <button type="button" class="btn-close" @click="selectedTooth = null"></button>
          </div>
          <div class="card-body">
            <div class="mb-3">
              <label class="form-label">Condición</label>
              <select 
                class="form-select" 
                v-model="selectedTooth.condition"
                :disabled="!isEditable"
              >
                <option value="sano">Sano</option>
                <option value="ausente">Ausente</option>
                <option value="extraccion">Extracción Indicada</option>
                <option value="caries">Caries</option>
                <option value="corona">Corona</option>
                <option value="restauracion">Restauración</option>
                <option value="implante">Implante</option>
                <option value="endodoncia">Endodoncia</option>
              </select>
            </div>
            <div class="mb-3">
              <label class="form-label">Observaciones</label>
              <textarea 
                class="form-control" 
                rows="3" 
                v-model="selectedTooth.observation"
                :disabled="!isEditable"
              ></textarea>
            </div>
            <div v-if="isEditable" class="d-flex justify-content-end">
              <button class="btn btn-primary" @click="saveTooth">Guardar</button>
            </div>
          </div>
        </div>
      </div>
  
      <!-- Legend -->
      <div class="odontogram-legend">
        <div class="legend-item">
          <div class="legend-color sano"></div>
          <span>Sano</span>
        </div>
        <div class="legend-item">
          <div class="legend-color ausente"></div>
          <span>Ausente</span>
        </div>
        <div class="legend-item">
          <div class="legend-color extraccion"></div>
          <span>Extracción Indicada</span>
        </div>
        <div class="legend-item">
          <div class="legend-color caries"></div>
          <span>Caries</span>
        </div>
        <div class="legend-item">
          <div class="legend-color corona"></div>
          <span>Corona</span>
        </div>
        <div class="legend-item">
          <div class="legend-color restauracion"></div>
          <span>Restauración</span>
        </div>
        <div class="legend-item">
          <div class="legend-color implante"></div>
          <span>Implante</span>
        </div>
        <div class="legend-item">
          <div class="legend-color endodoncia"></div>
          <span>Endodoncia</span>
        </div>
      </div>
    </div>
  </div>
</template>
  
<script>
import { toothService } from '@/services/api';
  
export default {
  name: 'Odontogram',
  props: {
    odontogramId: {
      type: [Number, String],
      required: true
    },
    isEditable: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: true,
      teeth: [],
      selectedTooth: null,
      // Adult teeth numbers from left to right (patient perspective)
      adultUpperTeeth: ['18', '17', '16', '15', '14', '13', '12', '11', '21', '22', '23', '24', '25', '26', '27', '28'],
      adultLowerTeeth: ['48', '47', '46', '45', '44', '43', '42', '41', '31', '32', '33', '34', '35', '36', '37', '38'],
      // Child teeth numbers 
      childUpperTeeth: ['55', '54', '53', '52', '51', '61', '62', '63', '64', '65'],
      childLowerTeeth: ['85', '84', '83', '82', '81', '71', '72', '73', '74', '75']
    };
  },
  async created() {
    try {
      await this.fetchTeeth();
    } catch (error) {
      console.error('Error loading odontogram data:', error);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async fetchTeeth() {
      // Fetch all teeth for this odontogram
      const response = await toothService.getAll();
      this.teeth = response.data.filter(
        tooth => tooth.odontogram === parseInt(this.odontogramId)
      );
      
      // Ensure all teeth exist with default values
      this.ensureAllTeethExist();
    },
    ensureAllTeethExist() {
      const allAdultTeethNumbers = [...this.adultUpperTeeth, ...this.adultLowerTeeth];
      const allChildTeethNumbers = [...this.childUpperTeeth, ...this.childLowerTeeth];
      const existingTeethNumbers = this.teeth.map(tooth => tooth.tooth_number);
      
      // Create default entries for missing adult teeth
      allAdultTeethNumbers.forEach(toothNumber => {
        if (!existingTeethNumbers.includes(toothNumber)) {
          this.teeth.push({
            odontogram: parseInt(this.odontogramId),
            tooth_number: toothNumber,
            type: 'adult',
            condition: 'sano',
            observation: ''
          });
        }
      });

      // Create default entries for missing child teeth
      allChildTeethNumbers.forEach(toothNumber => {
        if (!existingTeethNumbers.includes(toothNumber)) {
          this.teeth.push({
            odontogram: parseInt(this.odontogramId),
            tooth_number: toothNumber,
            type: 'child',
            condition: 'sano',
            observation: ''
          });
        }
      });
    },
    getToothClass(toothNumber, type) {
      const tooth = this.teeth.find(t => 
        t.tooth_number === toothNumber && 
        (t.type === type || !t.type)
      );
      if (!tooth) return 'sano';
      
      const conditionClasses = {
        'sano': 'sano',
        'ausente': 'ausente',
        'extraccion': 'extraccion',
        'caries': 'caries',
        'corona': 'corona',
        'restauracion': 'restauracion',
        'implante': 'implante',
        'endodoncia': 'endodoncia'
      };
      
      return conditionClasses[tooth.condition] || 'sano';
    },
    selectTooth(toothNumber, type) {
      const tooth = this.teeth.find(t => 
        t.tooth_number === toothNumber && 
        (t.type === type || !t.type)
      );
      if (tooth) {
        // Create a copy to avoid directly modifying the original before save
        this.selectedTooth = { 
          ...tooth,
          type: type
        };
      }
    },
    async saveTooth() {
      if (!this.selectedTooth) return;
      
      try {
        // If the tooth has an ID, it exists in the database, so update it
        if (this.selectedTooth.id) {
          await toothService.update(this.selectedTooth.id, this.selectedTooth);
        } else {
          // Otherwise create a new one
          await toothService.create(this.selectedTooth);
        }
        
        // Update the tooth in our local array
        const index = this.teeth.findIndex(t => 
          t.tooth_number === this.selectedTooth.tooth_number && 
          (t.type === this.selectedTooth.type || !t.type)
        );
        if (index !== -1) {
          this.teeth[index] = { ...this.selectedTooth };
        } else {
          this.teeth.push({ ...this.selectedTooth });
        }
        
        // Close the tooth info panel
        this.selectedTooth = null;
      } catch (error) {
        console.error('Error saving tooth data:', error);
        // We could show an error message here
      }
    }
  }
};
</script>
  
<style scoped>
.odontogram-container {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
}

.odontogram {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 1000px;
}

.odontogram-section {
  width: 100%;
  margin-bottom: 20px;
}

.section-title {
  text-align: center;
  margin-bottom: 15px;
  color: #495057;
}

.jaw {
  display: flex;
  justify-content: center;
  width: 100%;
  max-width: 1000px;
  margin-bottom: 15px;
  padding: 10px;
  border-radius: 10px;
  background-color: #f8f9fa;
}

.teeth-row {
  display: flex;
  justify-content: center;
  width: 100%;
}

.tooth-container {
  display: flex;
  justify-content: center;
  align-items: center;
  width: calc(100% / 16); /* Ensures exactly 16 teeth per row */
  padding: 5px;
  box-sizing: border-box;
}

.tooth {
  width: 40px;
  height: 40px;
  border: 2px solid #495057;
  border-radius: 5px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  position: relative;
  background-color: #fff;
  transition: all 0.2s ease;
}

.tooth:hover {
  transform: scale(1.1);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
}

.tooth-number {
  font-size: 0.8rem;
  font-weight: bold;
  color: #212529;
}

/* Tooth conditions with pastel colors */
.tooth.sano { background-color: #ffffff; } /* White */
.tooth.ausente { 
  background-color: #d3d3d3; /* Light Gray */
  border-style: dashed;
}
.tooth.extraccion {
  background-color: #ff9a8c; /* Coral Red - more distinct from pink */
  border-color: #dc3545;
}
.tooth.caries {
  background-color: #fffacd; /* Pastel Yellow */
  border-color: #ffc107;
}
.tooth.corona {
  background-color: #c1e1c1; /* Pastel Green */
  border-color: #198754;
}
.tooth.restauracion {
  background-color: #add8e6; /* Light Blue */
  border-color: #0d6efd;
}
.tooth.implante {
  background-color: #ffdab9; /* Pastel Orange */
  border-color: #fd7e14;
}
.tooth.endodoncia {
  background-color: #ffc0cb; /* Pastel Pink */
  border-color: #e83e8c;
}

.tooth-info-panel {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
  width: 300px;
}

.odontogram-legend {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 20px;
  border-top: 1px solid #dee2e6;
  padding-top: 20px;
}

.legend-item {
  display: flex;
  align-items: center;
  margin: 5px 10px;
}

.legend-color {
  width: 20px;
  height: 20px;
  border: 1px solid #495057;
  border-radius: 3px;
  margin-right: 5px;
}

/* Legend colors with pastel colors */
.legend-color.sano { background-color: #ffffff; } /* White */
.legend-color.ausente { 
  background-color: #d3d3d3; /* Light Gray */
  border-style: dashed;
}
.legend-color.extraccion {
  background-color: #ff9a8c; /* Coral Red - more distinct from pink */
  border-color: #dc3545;
}
.legend-color.caries {
  background-color: #fffacd; /* Pastel Yellow */
  border-color: #ffc107;
}
.legend-color.corona {
  background-color: #c1e1c1; /* Pastel Green */
  border-color: #198754;
}
.legend-color.restauracion {
  background-color: #add8e6; /* Light Blue */
  border-color: #0d6efd;
}
.legend-color.implante {
  background-color: #ffdab9; /* Pastel Orange */
  border-color: #fd7e14;
}
.legend-color.endodoncia {
  background-color: #ffc0cb; /* Pastel Pink */
  border-color: #e83e8c;
}
</style>